/**
 * Client-side recency for custom watchlists (add movie / note) so lists stay
 * ordered correctly even if Firestore timestamps lag or parse inconsistently.
 */
const STORAGE_KEY = 'watchlistUiRecency';
const MAX_ENTRIES = 80;

const VIRTUAL_DEFAULT_IDS = new Set([
  'want-to-watch',
  'watched',
  'main-watchlist',
  'your-reviews',
]);

export function toUpdatedAtMs(dateValue: unknown): number {
  if (dateValue == null) return 0;
  if (dateValue instanceof Date) {
    const t = dateValue.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof dateValue === 'object' && 'toDate' in (dateValue as object)) {
    const maybe = (dateValue as { toDate?: () => Date }).toDate;
    if (typeof maybe === 'function') {
      const d = maybe.call(dateValue);
      const t = d.getTime();
      return Number.isNaN(t) ? 0 : t;
    }
  }
  if (typeof dateValue === 'number' && !Number.isNaN(dateValue)) {
    return dateValue;
  }
  const d = new Date(String(dateValue));
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function touchWatchlistRecency(watchlistId: string): void {
  if (!watchlistId || VIRTUAL_DEFAULT_IDS.has(watchlistId)) {
    return;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};
    map[watchlistId] = Date.now();
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      keys
        .sort((a, b) => map[b] - map[a])
        .slice(MAX_ENTRIES)
        .forEach((k) => {
          delete map[k];
        });
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getWatchlistRecencyScore(watchlistId: string): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};
    return map[watchlistId] || 0;
  } catch {
    return 0;
  }
}

/** Higher = more recently used for sorting (desc order). */
export function watchlistInteractionRank(watchlistId: string, updatedAt: unknown): number {
  return Math.max(toUpdatedAtMs(updatedAt), getWatchlistRecencyScore(watchlistId));
}

/** Latest touch on a list: doc updates or any movie addedAt (whichever is most recent). */
export function getWatchlistLastActivityMs(w: {
  updatedAt?: string;
  createdAt?: string;
  movieDetails?: Array<{ addedAt?: string }>;
}): number {
  let t = 0;
  const bump = (s?: string) => {
    if (!s) return;
    const x = new Date(s).getTime();
    if (!Number.isNaN(x)) t = Math.max(t, x);
  };
  bump(w.updatedAt);
  bump(w.createdAt);
  for (const m of w.movieDetails || []) {
    bump(m.addedAt);
  }
  return t;
}
